import { useEffect, useRef, useState } from "react";

const ACTION_LABELS = {
  create: "Creato",
  update: "Aggiornato",
  delete: "Eliminato",
  sign: "Firma apposta",
  approve: "DVR approvato",
  eidas_prepare: "Pacchetto eIDAS preparato",
  eidas_finalize: "DVR firmato eIDAS",
  import_csv: "Import CSV",
};

const ACTION_TONES = {
  create: "emerald",
  update: "amber",
  delete: "rose",
  sign: "blue",
  approve: "purple",
  eidas_prepare: "indigo",
  eidas_finalize: "indigo",
  import_csv: "cyan",
};

/**
 * Connects to the backend WebSocket and exposes a queue of audit notifications.
 * Auto-reconnects on disconnect with exponential backoff capped at 30s.
 */
export function useNotifications() {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  useEffect(() => {
    const token = localStorage.getItem("dvr_token");
    if (!token) return;

    let stopped = false;

    const connect = () => {
      if (stopped) return;
      const httpUrl = process.env.REACT_APP_BACKEND_URL;
      const wsUrl = httpUrl.replace(/^http/, "ws") + `/api/ws/notifications?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        retryRef.current = 0;
      };
      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          // Fan out non-audit messages via CustomEvent for other components to listen
          if (data.type === "diff_comment" || data.type === "diff_comment_mention") {
            try {
              window.dispatchEvent(new CustomEvent(`dvr-${data.type}`, { detail: data }));
            } catch { /* noop */ }
            return;
          }
          if (data.type === "dvr_revision") {
            // Real-time notification per nuove revisioni DVR
            const action = data.action || "created";
            setEvents((prev) => [
              {
                id: `rev-${data.revision_id}-${Date.now()}`,
                action: `revision_${action}`,
                entity_type: "dvr_revision",
                label: `Nuova revisione DVR v${data.revision_number}`,
                tone: "indigo",
                user_name: data.created_by_name || "—",
                created_at: new Date().toISOString(),
                seen: false,
                extra: `Motivo: ${data.reason || "—"} · Scadenza firma: ${data.deadline || "—"}`,
              },
              ...prev,
            ].slice(0, 30));
            try {
              window.dispatchEvent(new CustomEvent("dvr-dvr_revision", { detail: data }));
            } catch { /* noop */ }
            return;
          }
          if (data.type === "audit") {
            const label = ACTION_LABELS[data.action] || data.action;
            const tone = ACTION_TONES[data.action] || "slate";
            setEvents((prev) => [
              {
                id: `${data.payload?.entity_id || Math.random()}-${Date.now()}`,
                action: data.action,
                entity_type: data.entity_type,
                label,
                tone,
                user_name: data.payload?.user_name || "—",
                created_at: data.payload?.created_at || new Date().toISOString(),
                seen: false,
              },
              ...prev,
            ].slice(0, 30));
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false);
        if (stopped) return;
        retryRef.current = Math.min(retryRef.current + 1, 6);
        const delay = Math.min(1000 * 2 ** retryRef.current, 30000);
        setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    connect();
    return () => {
      stopped = true;
      if (wsRef.current) try { wsRef.current.close(); } catch { /* noop */ }
    };
  }, []);

  const markAllSeen = () => setEvents((prev) => prev.map((e) => ({ ...e, seen: true })));
  const clear = () => setEvents([]);
  const unseenCount = events.filter((e) => !e.seen).length;

  return { events, connected, unseenCount, markAllSeen, clear };
}
